import { useTranslation } from "react-i18next";
import { Table, TableCell, TableHead, TableRow, TableBody } from "@mui/material";
import { formatStringCurrency } from "../../common/CurrencyFormatter";
import { AccountingAmount, ShallowRound } from "../../models/round";
import { getNumFixtures } from "../../common/roundhelpers";
import { IsOrangeLight, themeC } from "../../theme/themehelper";

interface ResultRowTrProps {
  index: number;
  payoutPerRow: AccountingAmount;
  maxNum: number;
  isRightToLeft?: boolean;
  rows?: string;
}

function ResultRowTr({
  index,
  payoutPerRow,
  maxNum,
  isRightToLeft,
  rows,
}: ResultRowTrProps) {
  const { t } = useTranslation();

  const { money } = payoutPerRow;
  const actualPayout = money.amount;
  const numCorrect = maxNum - index;

  let rowSx = {
    fontSize: "11px",
    padding: "5px 14px 5px 14px",
    color: "var(--text-black)",
  };

  if (index % 2 === 0) {
    rowSx = {
      fontSize: "11px",
      padding: "5px 14px 5px 14px",
      color: "var(--text-black-dark-background)",
    };
  }

  const alternating = {
    "&:nth-of-type(odd)": {
      backgroundColor: "var(--background_1)",
    },
    "&:nth-of-type(even)": {
      backgroundColor: "var(--background_2)",
    },
  };

  const whole = money.currency === "SEK";

  return (
    <TableRow sx={alternating}>
      {isRightToLeft ? (
        <>
          <TableCell align="left" sx={rowSx}>
            {formatStringCurrency(actualPayout, money.currency, t, whole)}
          </TableCell>
          <TableCell align="right" sx={rowSx}>
            {rows ? (Number(rows) < 1 ? Math.ceil(Number(rows)) : Math.round(Number(rows))) : "NA"} {t("st")}
          </TableCell>
          <TableCell align="right" sx={rowSx}>
            {numCorrect}
          </TableCell>
        </>
      ) : (
        <>
          <TableCell sx={rowSx}>{numCorrect}</TableCell>
          <TableCell sx={rowSx}>
            {rows ? (Number(rows) < 1 ? Math.ceil(Number(rows)) : Math.round(Number(rows))) : "NA"} {t("st")}
          </TableCell>
          <TableCell align="right" sx={rowSx}>
            {formatStringCurrency(actualPayout, money.currency, t, whole)}
          </TableCell>
        </>
      )}
    </TableRow>
  );
}

interface WinInfoRoundProps {
  round?: ShallowRound;
  isRightToLeft?: boolean;
}

export default function WinInfoRound({ round, isRightToLeft }: WinInfoRoundProps) {
  const { t } = useTranslation();
  const { operatorPrizePlan } = round || {};
  const numFixtures = round ? getNumFixtures(round.name) : 0;
  const groups = operatorPrizePlan?.payoutGroups || [];

  const headerSx = {
    fontSize: "11px",
    fontWeight: "bold",
    color: "var(--text-white)",
    padding: "5px 14px 5px 14px",
  };

  return groups.length ? (
    <Table>
      <TableHead
        sx={{
          background: window.theme === themeC.Green ? "#727272" : IsOrangeLight(window.theme) ? "#EDF1F7" : "var(--off-black)",
        }}
      >
        {isRightToLeft ? (
          <TableRow>
            <TableCell align="left" sx={headerSx}>
              {t("prize")}
            </TableCell>
            <TableCell align="right" sx={headerSx}>
              {t("winners")}
            </TableCell>
            <TableCell align="right" sx={headerSx}>
              {t("correct_rows")}
            </TableCell>
          </TableRow>
        ) : (
          <TableRow>
            <TableCell sx={headerSx}>{t("correct_rows")}</TableCell>
            <TableCell sx={headerSx}>{t("winners")}</TableCell>
            <TableCell align="right" sx={headerSx}>
              {t("prize")}
            </TableCell>
          </TableRow>
        )}
      </TableHead>
      <TableBody>
        {groups.map((group, i) => (
          <ResultRowTr
            key={`${i} result row`}
            maxNum={numFixtures}
            index={i}
            isRightToLeft={isRightToLeft}
            {...group}
          />
        ))}
      </TableBody>
    </Table>
  ) : (
    <></>
  );
}